import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import dayjs from 'dayjs';
import '../css/KenterAdmin.css';
import api from '../components/api';
import BeheerdersportaalSidebar from '../components/BeheerdersportaalSidebar';

/**
 * Admin page for managing Kenter API data and degree days
 * This page allows manual syncing and viewing of energy data
 * Enhanced for better mobile responsiveness and improved gas comparison sync
 */
const KenterAdmin = () => {
  const [isSyncing, setIsSyncing] = useState(false);
  const [isDaysSyncing, setIsDaysSyncing] = useState(false);
  const [lastUpdate, setLastUpdate] = useState(null);
  const [syncLog, setSyncLog] = useState('');
  const [daysSyncLog, setDaysSyncLog] = useState('');
  const [months, setMonths] = useState(12);
  const [usageData, setUsageData] = useState(null);
  const [showRawData, setShowRawData] = useState(false);
  const [degreeDaysData, setDegreeDaysData] = useState(null);
  const [degreeDaysCount, setDegreeDaysCount] = useState(0);
  const [degreeDaysLastUpdate, setDegreeDaysLastUpdate] = useState(null);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  // Gas comparison data state
  const [gasComparisonData, setGasComparisonData] = useState(null);
  const [gasComparisonLastMonth, setGasComparisonLastMonth] = useState(null);
  
  // New state variables for enhanced gas comparison sync (matching IstaAdminPanel)
  const [showSuccessMessage, setShowSuccessMessage] = useState(false);
  const [successStats, setSuccessStats] = useState(null);
  const [activeSection, setActiveSection] = useState('gas'); // Track which section is showing logs
  
  // Date range for degree days sync - default to a year before the day before yesterday
  const twoDaysAgo = dayjs().subtract(2, 'day');
  const [startDate, setStartDate] = useState(twoDaysAgo.subtract(2, 'month').format('YYYY-MM-DD'));
  const [endDate, setEndDate] = useState(twoDaysAgo.format('YYYY-MM-DD'));
  
  // Add window resize handler for responsive design
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);
  
  // Auto-hide success message after 10 seconds (matching IstaAdminPanel)
  useEffect(() => {
    let timer;
    if (showSuccessMessage) {
      timer = setTimeout(() => {
        setShowSuccessMessage(false);
      }, 10000); // 10 seconds
    }
    
    return () => {
      if (timer) clearTimeout(timer);
    };
  }, [showSuccessMessage]);
  
  // Fetch data on component mount
  useEffect(() => {
    fetchLastUpdate();
    fetchUsageData();
    fetchDegreeDaysData();
    fetchGasComparisonData();
    fetchGasComparisonLastMonth();
  }, []);
  
  const fetchLastUpdate = async () => {
    try {
      const response = await api.get('/api/kenter/last-update');
      if (response.data && response.data.lastUpdate) {
        setLastUpdate(new Date(response.data.lastUpdate).toLocaleString());
      }
    } catch (error) {
      console.error('Error fetching last update:', error);
    }
  };
  
  const fetchUsageData = async () => {
    try {
      // Explicitly specify starting from 2021 to include 2021-2022 energy year
      const response = await api.get('/api/kenter/usage', {
        params: {
          startYear: 2020,
          endYear: new Date().getFullYear()
        }
      });
      const dataWithPredictions = addPredictionsToData(response.data);
      setUsageData(dataWithPredictions);
    } catch (error) {
      console.error('Error fetching usage data:', error);
    }
  };
  
  // Function to fetch gas comparison data from MongoDB - enhanced with error handling
  const fetchGasComparisonData = async () => {
    try {
      const response = await api.get('/api/energy/gas-comparison');
      
      // Check the structure of the response to determine how to process it
      if (response.data && Array.isArray(response.data)) {
        setGasComparisonData(response.data);
      } else if (response.data && response.data.success === true && Array.isArray(response.data.data)) {
        setGasComparisonData(response.data.data);
      } else {
        console.error('Unexpected gas comparison data format:', response.data);
        setGasComparisonData([]);
      }
    } catch (error) {
      console.error('Error fetching gas comparison data:', error);
      setGasComparisonData([]);
    }
  };
  
  // Function to fetch last available month for gas comparison
// Function to fetch last available month for gas comparison with improved error handling
const fetchGasComparisonLastMonth = async () => {
  try {
    console.log('Fetching last available month for gas comparison...');
    
    // Use the endpoint to get the last month data
    const response = await api.get('/api/energy/gas-comparison/last-month');
    
    // Log the full response for debugging
    console.log('Last month API response:', response);
    
    // Handle the response
    if (response.data) {
      console.log('Last month data:', response.data);
      
      // If we have month and year data, store it
      if (response.data.lastMonth && response.data.lastYear) {
        setGasComparisonLastMonth({
          ...response.data,
          success: true
        });
        
        console.log(`✅ Found latest data: ${response.data.lastMonth}/${response.data.lastYear}`);
        
        // Auto-set months for convenient updating
        const currentDate = dayjs();
        const lastDataDate = dayjs(`${response.data.lastYear}-${response.data.lastMonth.toString().padStart(2, '0')}-01`);
        const monthsDiff = currentDate.diff(lastDataDate, 'month');
        
        if (monthsDiff > 0 && months === 12) { // Only if using default value
          setMonths(Math.max(1, monthsDiff));
          console.log(`Auto-setting months to ${monthsDiff} based on last data`);
        }
      } else {
        console.log('⚠️ No month/year data found in response');
        
        // Check if we successfully processed some data but don't have a last month
        // This can happen if the data was processed but not properly saved
        if (syncLog && syncLog.includes('successfully') && syncLog.includes('Processed')) {
          // Try to extract month/year from the sync log
          const processedMonthsMatch = syncLog.match(/Months Processed:\s+(\d+)/i);
          const updatedYearsMatch = syncLog.match(/Years Updated:\s+\d+\s+\(([\d-]+)/i);
          
          if (processedMonthsMatch && updatedYearsMatch) {
            console.log('Trying to extract month/year from sync log');
            
            const currentDate = dayjs();
            const energyYear = updatedYearsMatch[1];
            
            // Use the current month as an approximation
            setGasComparisonLastMonth({
              success: true,
              lastMonth: currentDate.month() + 1, // 0-indexed to 1-indexed
              lastYear: currentDate.year(),
              energyYear: energyYear,
              lastUpdated: new Date(),
              extractedFromLog: true // Flag that this was extracted
            });
            
            console.log('Extracted month/year from sync log');
          } else {
            // Fall back to basic info
            setGasComparisonLastMonth({
              success: response.data.success,
              lastUpdated: response.data.lastUpdated || new Date(),
              message: 'Data processed but no month info available'
            });
          }
        } else {
          // Regular case - just use what we got
          setGasComparisonLastMonth({
            success: response.data.success,
            lastUpdated: response.data.lastUpdated || new Date(),
            message: response.data.message || 'No data available'
          });
        }
      }
    } else {
      console.log('⚠️ Empty response from last-month endpoint');
      setGasComparisonLastMonth({
        success: false,
        message: 'Empty response from server',
        lastUpdated: new Date()
      });
    }
  } catch (error) {
    console.error('Error fetching gas comparison last month:', error);
    setGasComparisonLastMonth({
      success: false,
      error: error.message,
      message: 'Error connecting to server',
      lastUpdated: new Date()
    });
  }
};
  
  const fetchDegreeDaysData = async () => {
    try {
      // Get count of degree days documents
      const countResponse = await api.get('/api/degree-days/count');
      
      // Set the count from the response
      setDegreeDaysCount(countResponse.data.count || 0);
      
      // Check if lastUpdate exists in the response and set it
      if (countResponse.data && countResponse.data.lastUpdate) {
        // Format the date nicely
        setDegreeDaysLastUpdate(dayjs(countResponse.data.lastUpdate).format('D MMMM YYYY'));
      } else {
        setDegreeDaysLastUpdate(null);
      }
      
      // Get sample data for display
      if (countResponse.data.count > 0) {
        const dataResponse = await api.get('/api/degree-days/summary');
        setDegreeDaysData(dataResponse.data);
      }
    } catch (error) {
      console.error('Error fetching degree days data:', error);
    }
  };
  
  // Improved prediction calculation function
  const calculatePrediction = (yearData) => {
    // Seasonality map - what percentage of annual consumption has occurred by each month
    const seasonalityMap = {
      '07': 0.01, '08': 0.03, '09': 0.05, '10': 0.12,
      '11': 0.24, '12': 0.40, '01': 0.57, '02': 0.71,
      '03': 0.84, '04': 0.93, '05': 0.98, '06': 1.00
    };

    // Skip if no monthly data
    if (!yearData || !yearData.monthly || Object.keys(yearData.monthly).length === 0) {
      return null;
    }

    // Find the latest month with data
    const monthsWithData = Object.keys(yearData.monthly)
      .filter(month => month.match(/^\d{4}-\d{2}$/) && yearData.monthly[month] > 0)
      .map(monthKey => {
        const [year, month] = monthKey.split('-');
        // Convert to a sortable format
        return {
          key: monthKey,
          sortValue: parseInt(`${year}${month}`),
          month: month
        };
      })
      .sort((a, b) => b.sortValue - a.sortValue); // Sort in descending order

    // If no months with data, cannot predict
    if (monthsWithData.length === 0) {
      return null;
    }

    // Get the latest month
    const latestMonth = monthsWithData[0];
    
    // Get the seasonality factor for this month
    const seasonFactor = seasonalityMap[latestMonth.month];
    if (!seasonFactor) {
      return null;
    }

    // Calculate current usage to date
    const currentUsage = Object.values(yearData.monthly).reduce((sum, val) => sum + (val || 0), 0);
    
    // Only make prediction if we have a non-zero seasonality factor
    if (seasonFactor <= 0) {
      return currentUsage; // Just return current if we can't predict
    }

    // Calculate prediction by dividing current usage by the seasonality factor
    const predictedTotal = Math.round(currentUsage / seasonFactor);
    
    return predictedTotal;
  };
  
  // Function to add predictions to all years in the data
  const addPredictionsToData = (data) => {
    if (!data) return data;
    
    const result = { ...data };
    
    // Calculate predictions for each year
    Object.keys(data).forEach(year => {
      const yearData = data[year];
      if (yearData && yearData.monthly) {
        // Add prediction using our improved function
        result[year] = {
          ...yearData,
          prediction: calculatePrediction(yearData)
        };
      }
    });
    
    return result;
  };
  
  const handleSync = async () => {
    setIsSyncing(true);
    setSyncLog('Syncing with Kenter API...');
    setActiveSection('kenter');
    
    try {
      const response = await api.get('/api/kenter/sync', {
        params: { months }
      });
      
      setSyncLog(JSON.stringify(response.data, null, 2));
      
      // Refresh data
      await fetchLastUpdate();
      await fetchUsageData();
    } catch (error) {
      setSyncLog(`Error syncing: ${error.message}`);
      console.error('Error syncing with Kenter API:', error);
    } finally {
      setIsSyncing(false);
    }
  };
  
  // ENHANCED function to refresh gas comparison data - based on IstaAdminPanel.js
const handleRefreshGasComparison = async () => {
  setIsSyncing(true);
  setSyncLog('Refreshing gas comparison data...');
  setShowSuccessMessage(false);
  setActiveSection('gas');
  
  try {
    // Call the API endpoint to refresh gas comparison data
    const res = await api.get('/api/energy/refresh-gas-comparison', {
      params: {
        months: months === 0 ? undefined : months
      }
    });
    
    // Prepare a nicely formatted log message
    let logMessage = `✅ Gas comparison data refreshed successfully!\n\n`;
    
    if (res.data && res.data.stats) {
      logMessage += `Stats:\n`;
      logMessage += `- Data Range: ${res.data.stats.monthRange || 'N/A'}\n`;
      logMessage += `- Months Processed: ${res.data.stats.processedMonths || 0}\n`;
      logMessage += `- Years Updated: ${res.data.stats.updatedYears?.length || 0} (${res.data.stats.updatedYears?.join(', ') || 'none'})\n`;
      
      if (res.data.stats.newYears && res.data.stats.newYears.length > 0) {
        logMessage += `- New Years Added: ${res.data.stats.newYears.length} (${res.data.stats.newYears.join(', ')})\n`;
      }
      
      logMessage += `- Processing Time: ${res.data.stats.processingTime || 'n/a'}\n`;
      
      // Add info about future months
      if (res.data.stats.futureMonthsSkipped) {
        logMessage += `- Future Months Skipped: ${res.data.stats.futureMonthsSkipped}\n`;
      }
      
      // Handle errors with better explanation
      if (res.data.stats.errors > 0) {
        logMessage += `- Errors: ${res.data.stats.errors}\n`;
        
        // If we have specific future month errors, explain them
        if (res.data.stats.futureMonthErrors) {
          logMessage += `  Note: ${res.data.stats.futureMonthErrors} errors were due to attempting to fetch future months.\n`;
          logMessage += `  This is normal - data for future months is not available yet.\n`;
        } else {
          logMessage += `  (see server logs for details)\n`;
        }
      }
      
      setSuccessStats({
        ...res.data.stats,
        hasFutureMonthErrors: (res.data.stats.futureMonthErrors > 0 || res.data.stats.futureMonthsSkipped > 0)
      });
      setShowSuccessMessage(true);
      
      // Refresh last gas month info after successful update
      try {
        const monthRes = await api.get('/api/energy/gas-comparison/last-month');
        if (monthRes.data && monthRes.data.success) {
          const month = monthRes.data.lastMonth;
          const year = monthRes.data.lastYear;
          
          if (month && year) {
            logMessage += `\nData is now up to date through ${getMonthName(month)} ${year}.`;
          }
        }
      } catch (err) {
        console.error('Error updating last gas month info:', err);
      }
    }
    
    setSyncLog(logMessage);
    
    // Refresh gas comparison data
    await fetchGasComparisonData();
    await fetchGasComparisonLastMonth();
  } catch (error) {
    setSyncLog(`❌ Error refreshing gas comparison data: ${error.response?.data?.error || error.message}`);
    console.error('Error refreshing gas comparison:', error);
  } finally {
    setIsSyncing(false);
  }
};
  
  const handleSyncDegreeDays = async () => {
    setIsDaysSyncing(true);
    setDaysSyncLog('Syncing degree days data from Open-Meteo Archive API...');
    setActiveSection('degreedays');
    
    try {
      // Ensure end date is not after the day before yesterday
      const twoDaysAgo = dayjs().subtract(2, 'day');
      const safeEndDate = dayjs(endDate).isAfter(twoDaysAgo) ? 
        twoDaysAgo.format('YYYY-MM-DD') : endDate;
      
      if (safeEndDate !== endDate) {
        setEndDate(safeEndDate);
        setDaysSyncLog(`Adjusted end date to ${safeEndDate} (data only available until the day before yesterday)`);
      }
      
      const response = await api.get('/api/degree-days/sync-archive', {
        params: { 
          start: startDate,
          end: safeEndDate
        }
      });
      
      setDaysSyncLog(JSON.stringify(response.data, null, 2));
      
      // Refresh degree days data after sync
      await fetchDegreeDaysData();
    } catch (error) {
      setDaysSyncLog(`Error syncing degree days: ${error.message}`);
      console.error('Error syncing degree days:', error);
    } finally {
      setIsDaysSyncing(false);
    }
  };
  
  // Helper function to convert month number to month name - for gas comparison status
  const getMonthName = (monthNum) => {
    const monthNames = {
      1: 'januari', 2: 'februari', 3: 'maart', 4: 'april',
      5: 'mei', 6: 'juni', 7: 'juli', 8: 'augustus',
      9: 'september', 10: 'oktober', 11: 'november', 12: 'december'
    };
    return monthNames[monthNum] || monthNum;
  };
  
  // Helper function to calculate months behind - similar to IstaAdminPanel
// Helper function to calculate months behind - improved for all cases
const getMonthsNeededText = () => {
  // Handle the case where we have no data
  if (!gasComparisonLastMonth) {
    return 'No data available';
  }
  
  // Handle the case where data was extracted from sync log
  if (gasComparisonLastMonth.extractedFromLog) {
    return 'Recently processed data';
  }
  
  // Handle the case where we don't have month/year data
  if (!gasComparisonLastMonth.lastMonth || !gasComparisonLastMonth.lastYear) {
    return 'Status unknown';
  }
  
  // Calculate months behind for normal case
  const currentDate = dayjs();
  const lastDataMonth = gasComparisonLastMonth.lastMonth;
  const lastDataYear = gasComparisonLastMonth.lastYear;
  
  // Create date for first day of last data month
  const lastDataDate = dayjs(`${lastDataYear}-${lastDataMonth.toString().padStart(2, '0')}-01`);
  
  // Calculate difference in months
  const monthsDiff = currentDate.diff(lastDataDate, 'month');
  
  // Return appropriate text based on difference
  if (monthsDiff <= 0) {
    return 'Up to date!';
  } else if (monthsDiff === 1) {
    return `1 month behind (needs update)`;
  } else {
    return `${monthsDiff} months behind (update needed)`;
  }
};
  
  const formatNumber = (num) => {
    if (num === undefined || num === null) return 'N/A';
    return new Intl.NumberFormat('nl-NL').format(Math.round(num));
  };
  
  // Calculate percentage change between values
  const calculatePercentChange = (current, previous) => {
    if (!previous || previous === 0) return '∞';
    const change = ((current - previous) / previous) * 100;
    return change.toFixed(1);
  };
  
  // Check if a date is after the day before yesterday
  const isDateTooRecent = (dateStr) => {
    const date = dayjs(dateStr);
    const twoDaysAgo = dayjs().subtract(2, 'day');
    return date.isAfter(twoDaysAgo);
  };

  // Function to find total usage for a specific energy year (for efficiency calculation)
  const getTotalUsageForEnergyYear = (energyYear) => {
    if (!usageData || !energyYear) return null;
    
    // Check if this energy year exists in our data
    return usageData[energyYear]?.total || null;
  };

  // Function to calculate efficiency (usage per degree day)
  const calculateEfficiency = (usage, degreeDays) => {
    if (!usage || !degreeDays || degreeDays === 0) return null;
    return (usage / degreeDays).toFixed(2);
  };
  
  // Function to calculate efficiency per apartment
  const calculateEfficiencyPerApartment = (usage, degreeDays, numApartments = 62) => {
    if (!usage || !degreeDays || degreeDays === 0) return null;
    return ((usage / degreeDays) / numApartments).toFixed(2);
  };

  // Render the log section based on which section is active
  const renderLogSection = (section, logContent) => {
    if (!logContent || activeSection !== section) return null;
    
    return (
      <div className="sync-log">
        <h3>{section === 'gas' ? 'Gas Comparison Sync Log' : 
             section === 'kenter' ? 'Kenter API Sync Log' : 
             'Degree Days Sync Log'}</h3>
        <div className="log-container">
          <pre>{logContent}</pre>
        </div>
      </div>
    );
  };

  const ImprovedMonthlyBreakdownWithDegreeDays = ({ usageData, formatNumber }) => {
  const [degreeDaysData, setDegreeDaysData] = useState({});
  const [loadingDegreeDays, setLoadingDegreeDays] = useState(false);
  
  // Helper function to determine energy year from month string (YYYY-MM)
  const getEnergyYearFromMonth = (monthStr) => {
    const [year, month] = monthStr.split('-').map(Number);
    return month >= 7 ? `${year}-${year+1}` : `${year-1}-${year}`;
  };

  // Function to sort months chronologically within an energy year
  const sortEnergyYearMonths = (a, b) => {
    const [yearA, monthA] = a.split('-').map(Number);
    const [yearB, monthB] = b.split('-').map(Number);
    
    // Convert to normalized months within energy year (July = 1, June = 12)
    const normalizedMonthA = monthA >= 7 ? monthA - 6 : monthA + 6;
    const normalizedMonthB = monthB >= 7 ? monthB - 6 : monthB + 6;
    
    // First compare by year, then by normalized month
    return yearA !== yearB ? yearA - yearB : normalizedMonthA - normalizedMonthB;
  };

  // Function to format month names for display
  const formatMonthName = (monthStr) => {
    const [year, month] = monthStr.split('-');
    const monthNames = [
      'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 
      'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'
    ];
    return `${monthNames[parseInt(month)-1]} ${year}`;
  };

  // Function to fetch degree days for a specific energy year
  const fetchDegreeDaysForYear = async (energyYear) => {
    try {
      setLoadingDegreeDays(true);
      console.log(`🔍 Fetching degree days for energy year: ${energyYear}`);
      
      const response = await api.get('/api/degree-days/monthly', {
        params: { energyYear }
      });
      
      // Transform the response data to match our month format (YYYY-MM)
      const degreeDataByMonth = {};
      
      response.data.forEach(item => {
        // item._id is the month string like "2024-10"
        // item.totalDegreeDays is the sum for that month
        degreeDataByMonth[item._id] = Math.round(item.totalDegreeDays);
      });
      
      console.log(`✅ Degree days data for ${energyYear}:`, degreeDataByMonth);
      
      setDegreeDaysData(prev => ({
        ...prev,
        [energyYear]: degreeDataByMonth
      }));
      
    } catch (error) {
      console.error(`❌ Error fetching degree days for ${energyYear}:`, error);
    } finally {
      setLoadingDegreeDays(false);
    }
  };

  // Collect all monthly data across all years and group them by energy year
  const collectMonthlyDataByEnergyYear = () => {
    const energyYears = {};

    // Process each year's monthly data
    Object.keys(usageData || {}).forEach(yearKey => {
      const yearData = usageData[yearKey];
      if (!yearData || !yearData.monthly) return;

      // Process monthly data
      Object.entries(yearData.monthly).forEach(([month, usage]) => {
        // Skip non-month entries
        if (!month.match(/^\d{4}-\d{2}$/)) return;
        
        // Get energy year for this month
        const energyYear = getEnergyYearFromMonth(month);
        
        // Initialize energy year object if not exists
        if (!energyYears[energyYear]) {
          energyYears[energyYear] = {
            months: {},
            totalUsage: 0
          };
        }
        
        // Add month to the energy year
        energyYears[energyYear].months[month] = usage;
        energyYears[energyYear].totalUsage += usage;
      });
    });

    return energyYears;
  };

  // Get energy years with all their months
  const energyYearData = collectMonthlyDataByEnergyYear();
  
  // Sort energy years in descending order (newest first)
  const sortedEnergyYears = Object.keys(energyYearData).sort().reverse();

  // Fetch degree days when energy years change
  useEffect(() => {
    sortedEnergyYears.forEach(energyYear => {
      if (!degreeDaysData[energyYear]) {
        fetchDegreeDaysForYear(energyYear);
      }
    });
  }, [sortedEnergyYears.join(',')]);

  // Function to get degree days for a specific month
  const getDegreeDaysForMonth = (energyYear, month) => {
    const yearData = degreeDaysData[energyYear];
    if (!yearData) return 'Loading...';
    
    const degreeDays = yearData[month];
    return degreeDays !== undefined ? degreeDays : 'N/A';
  };

  // Function to calculate efficiency (usage per degree day)
  const calculateEfficiency = (usage, degreeDays) => {
    if (!usage || !degreeDays || degreeDays === 0 || degreeDays === 'N/A' || degreeDays === 'Loading...') {
      return 'N/A';
    }
    return (usage / degreeDays).toFixed(2);
  };

  return (
    <div className="monthly-breakdown-improved">
      <h3>Monthly Breakdown by Energy Year</h3>
      
      {sortedEnergyYears.length === 0 ? (
        <p>No monthly data available</p>
      ) : (
        <div className="energy-years-container">
          {sortedEnergyYears.map(energyYear => {
            const { months, totalUsage } = energyYearData[energyYear];
            const sortedMonths = Object.keys(months).sort(sortEnergyYearMonths);
            
            // Calculate total degree days for this energy year
            const yearDegreeDays = degreeDaysData[energyYear];
            const totalDegreeDays = yearDegreeDays ? 
              Object.values(yearDegreeDays).reduce((sum, dd) => sum + (dd || 0), 0) : 'Loading...';
            
            const yearEfficiency = totalDegreeDays !== 'Loading...' && totalDegreeDays > 0 ? 
              (totalUsage / totalDegreeDays).toFixed(2) : 'N/A';

            return (
              <div key={energyYear} className="year-breakdown">
                <h4>
                  {energyYear} (Total: {formatNumber(totalUsage)} m³)
                  {totalDegreeDays !== 'Loading...' && (
                    <div className="year-summary">
                      <span>Degree Days: {formatNumber(totalDegreeDays)}</span>
                      <span>Efficiency: {yearEfficiency} m³/DD</span>
                    </div>
                  )}
                </h4>
                <div className="table-responsive">
                  <table className="month-table">
                    <thead>
                      <tr>
                        <th>Month</th>
                        <th>Usage (m³)</th>
                        <th>Degree Days</th>
                        <th>Efficiency</th>
                      </tr>
                    </thead>
                    <tbody>
                      {sortedMonths.map(month => {
                        const usage = months[month];
                        const degreeDays = getDegreeDaysForMonth(energyYear, month);
                        const efficiency = calculateEfficiency(usage, degreeDays);
                        
                        return (
                          <tr key={month}>
                            <td>{formatMonthName(month)}</td>
                            <td>{formatNumber(usage)}</td>
                            <td>{typeof degreeDays === 'number' ? formatNumber(degreeDays) : degreeDays}</td>
                            <td>{efficiency}</td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
            );
          })}
        </div>
      )}
      
      {loadingDegreeDays && (
        <div className="loading-indicator">
          <p>Loading degree days data...</p>
        </div>
      )}
    </div>
  );
};

  // ----- IMPROVED MONTHLY BREAKDOWN COMPONENT -----
  const ImprovedMonthlyBreakdown = ({ usageData, formatNumber }) => {
    // Helper function to determine energy year from month string (YYYY-MM)
    const getEnergyYearFromMonth = (monthStr) => {
      const [year, month] = monthStr.split('-').map(Number);
      return month >= 7 ? `${year}-${year+1}` : `${year-1}-${year}`;
    };

    // Function to sort months chronologically within an energy year
    // July 2023 should come before June 2024 (both in energy year 2023-2024)
    const sortEnergyYearMonths = (a, b) => {
      const [yearA, monthA] = a.split('-').map(Number);
      const [yearB, monthB] = b.split('-').map(Number);
      
      // Convert to normalized months within energy year (July = 1, June = 12)
      const normalizedMonthA = monthA >= 7 ? monthA - 6 : monthA + 6;
      const normalizedMonthB = monthB >= 7 ? monthB - 6 : monthB + 6;
      
      // First compare by year, then by normalized month
      return yearA !== yearB ? yearA - yearB : normalizedMonthA - normalizedMonthB;
    };

    // Function to format month names for display
    const formatMonthName = (monthStr) => {
      const [year, month] = monthStr.split('-');
      const monthNames = [
        'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 
        'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'
      ];
      return `${monthNames[parseInt(month)-1]} ${year}`;
    };

    // Collect all monthly data across all years and group them by energy year
    const collectMonthlyDataByEnergyYear = () => {
      const energyYears = {};

      // Process each year's monthly data
      Object.keys(usageData || {}).forEach(yearKey => {
        const yearData = usageData[yearKey];
        if (!yearData || !yearData.monthly) return;

        // Process monthly data
        Object.entries(yearData.monthly).forEach(([month, usage]) => {
          // Skip non-month entries
          if (!month.match(/^\d{4}-\d{2}$/)) return;
          
          // Get energy year for this month
          const energyYear = getEnergyYearFromMonth(month);
          
          // Initialize energy year object if not exists
          if (!energyYears[energyYear]) {
            energyYears[energyYear] = {
              months: {},
              totalUsage: 0
            };
          }
          
          // Add month to the energy year
          energyYears[energyYear].months[month] = usage;
          energyYears[energyYear].totalUsage += usage;
        });
      });

      return energyYears;
    };

    // Get energy years with all their months
    const energyYearData = collectMonthlyDataByEnergyYear();
    
    // Sort energy years in descending order (newest first)
    const sortedEnergyYears = Object.keys(energyYearData).sort().reverse();

    return (
      <div className="monthly-breakdown-improved">
        <h3>Monthly Breakdown by Energy Year</h3>
        
        {sortedEnergyYears.length === 0 ? (
          <p>No monthly data available</p>
        ) : (
          <div className="energy-years-container">
            {sortedEnergyYears.map(energyYear => {
              const { months, totalUsage } = energyYearData[energyYear];
              const sortedMonths = Object.keys(months).sort(sortEnergyYearMonths);
              
              return (
                <div key={energyYear} className="year-breakdown">
                  <h4>{energyYear} (Total: {formatNumber(totalUsage)} m³)</h4>
                  <div className="table-responsive">
                    <table className="month-table">
                      <thead>
                        <tr>
                          <th>Month</th>
                          <th>Usage (m³)</th>
                        </tr>
                      </thead>
                      <tbody>
                        {sortedMonths.map(month => (
                          <tr key={month}>
                            <td>{formatMonthName(month)}</td>
                            <td>{formatNumber(months[month])}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    );
  };
  
  // New component to display Gas Comparison data
  const GasComparisonTable = ({ gasComparisonData }) => {
    // Constants for number of apartments
    const KRAAG_APARTMENTS = 14;
    const TOREN_APARTMENTS = 48;
    const TOTAL_APARTMENTS = KRAAG_APARTMENTS + TOREN_APARTMENTS;
    
    // Format GJ to a nicer number with 2 decimal places
    const formatGJ = (gj) => {
      if (gj === undefined || gj === null) return 'N/A';
      return new Intl.NumberFormat('nl-NL', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(gj);
    };
    
    // Calculate per apartment usage
    const calculatePerApartment = (usage, apartments) => {
      if (usage === undefined || usage === null || apartments === 0) return 'N/A';
      return (usage / apartments).toFixed(2);
    };
    
    // Calculate percentage difference
    const calculateDifference = (kraag, toren) => {
      if (kraag === undefined || kraag === null || toren === undefined || toren === null || toren === 0) {
        return 'N/A';
      }
      
      // Calculate percentage difference (how much more/less Kraag uses compared to Toren)
      const diff = ((kraag - toren) / toren) * 100;
      return `${diff.toFixed(1)}%`;
    };
    
    // Calculate year-over-year percentage change
    const calculateYoYChange = (current, previous) => {
      if (previous === undefined || previous === null || previous === 0) {
        return 'N/A';
      }
      
      const change = ((current - previous) / previous) * 100;
      return `${change.toFixed(1)}%`;
    };
    
    // Make sure gasComparisonData is an array and filter out any items without energyYear
    // Also filter out the 2020-2021 energy year
    const validData = Array.isArray(gasComparisonData) 
      ? gasComparisonData.filter(item => 
          item && 
          item.energyYear && 
          item.energyYear !== '2020-2021') // Remove 2020-2021 from the data
      : [];
    
    // Sort data by energy year in ascending order (oldest first)
    const sortedData = validData.sort((a, b) => {
      const yearA = parseInt(a.energyYear.split('-')[0], 10);
      const yearB = parseInt(b.energyYear.split('-')[0], 10);
      return yearA - yearB;
    });
    
    return (
      <div className="gas-comparison">
        <h3>Gas Usage Comparison: Kraag vs Toren</h3>
        
        <div className="table-responsive">
          <table className="usage-table">
            <thead>
              <tr>
                <th>Energy Year</th>
                <th>Kraag (GJ)</th>
                <th>Toren (GJ)</th>
                <th>Combined (GJ)</th>
                <th>YoY %</th>
                <th>Kraag/woning</th>
                <th>Toren/woning</th>
                <th>Verschil %</th>
              </tr>
            </thead>
            <tbody>
              {sortedData.map((item, index) => {
                const kraagPerApt = calculatePerApartment(item.kraagUsage, KRAAG_APARTMENTS);
                const torenPerApt = calculatePerApartment(item.torenUsage, TOREN_APARTMENTS);
                const combinedUsage = item.kraagUsage + item.torenUsage;
                const difference = calculateDifference(kraagPerApt, torenPerApt);
                
                // Calculate YoY change by comparing with previous year's data
                let yoyChange = 'N/A';
                let yoyClass = '';
                
                if (index > 0) {
                  const prevYearData = sortedData[index - 1];
                  const prevCombinedUsage = prevYearData.kraagUsage + prevYearData.torenUsage;
                  yoyChange = calculateYoYChange(combinedUsage, prevCombinedUsage);
                  
                  // Set class based on whether change is positive (increase in usage = bad) or negative (decrease = good)
                  if (yoyChange !== 'N/A') {
                    const yoyValue = parseFloat(yoyChange);
                    yoyClass = yoyValue < 0 ? 'negative-change' : 'positive-change';
                  }
                }
                
                // Determine class for verschil cell
                const diffClass = parseFloat(difference) < 0 ? 'negative-change' : 'positive-change';
                
                return (
                  <tr key={item.energyYear}>
                    <td>{item.energyYear}</td>
                    <td>{formatGJ(item.kraagUsage)}</td>
                    <td>{formatGJ(item.torenUsage)}</td>
                    <td>{formatGJ(combinedUsage)}</td>
                    <td className={yoyClass}>{yoyChange}</td>
                    <td>{kraagPerApt}</td>
                    <td>{torenPerApt}</td>
                    <td className={diffClass}>{difference}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        
        <div className="comparison-legend">
          <p><strong>Note:</strong> Gas usage is measured in Gigajoules (GJ)</p>
          <p><strong>Kraag:</strong> 14 apartments | <strong>Toren:</strong> 48 apartments</p>
          <p><strong>YoY %:</strong> Year-over-year percentage change in combined gas usage</p>
          <p><strong>Verschil %:</strong> Percentage difference between Kraag and Toren usage per apartment</p>
          <p className="positive-change">Positive values (red) indicate an increase or higher usage (unfavorable)</p>
          <p className="negative-change">Negative values (green) indicate a decrease or lower usage (favorable)</p>
        </div>
      </div>
    );
  };

  // Option B: Grouped Gas Comparison Monthly Component (2022 onwards)
const EnhancedGroupedGasComparisonMonthly = ({ gasComparisonData }) => {
  // Constants for number of apartments
  const KRAAG_APARTMENTS = 14;
  const TOREN_APARTMENTS = 48;
  
  // Conversion factor from your documentation: 1 GJ = 32.68 m³
  const GJ_TO_M3_CONVERSION = 32.68;
  
  // Format GJ to a nicer number with 2 decimal places
  const formatGJ = (gj) => {
    if (gj === undefined || gj === null || gj === 0) return '0.00';
    return new Intl.NumberFormat('nl-NL', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(gj);
  };

  // Format m³ to a nicer number with 1 decimal place
  const formatM3 = (m3) => {
    if (m3 === undefined || m3 === null || m3 === 0) return '0.0';
    return new Intl.NumberFormat('nl-NL', { minimumFractionDigits: 1, maximumFractionDigits: 1 }).format(m3);
  };

  // Convert GJ to m³
  const convertGJtoM3 = (gj) => {
    if (gj === undefined || gj === null || gj === 0) return 0;
    return gj * GJ_TO_M3_CONVERSION;
  };

  // Format month names for display
  const formatMonthName = (month, year) => {
    const monthNames = [
      'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 
      'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'
    ];
    return `${monthNames[month - 1]} ${year}`;
  };

  // Calculate percentage difference between Kraag and Toren per apartment
  const calculatePercentageDifference = (kraagPerApt, torenPerApt) => {
    if (!kraagPerApt || !torenPerApt || torenPerApt === 0) return 'N/A';
    const diff = ((kraagPerApt - torenPerApt) / torenPerApt) * 100;
    return `${diff.toFixed(1)}%`;
  };

  // Calculate verhouding (ratio) between Kraag and Toren usage
  const calculateVerhouding = (kraagUsage, torenUsage) => {
    const totalUsage = (kraagUsage || 0) + (torenUsage || 0);
    if (totalUsage === 0) return 'N/A';
    
    const kraagPercentage = ((kraagUsage || 0) / totalUsage) * 100;
    const torenPercentage = ((torenUsage || 0) / totalUsage) * 100;
    
    return `${kraagPercentage.toFixed(1)}% | ${torenPercentage.toFixed(1)}%`;
  };

  const getDifferenceClass = (difference) => {
    if (difference === 'N/A') return '';
    const value = parseFloat(difference);
    return value < 0 ? 'negative-change' : 'positive-change';
  };

  // Filter and sort data
  const validData = Array.isArray(gasComparisonData) 
    ? gasComparisonData.filter(item => item && item.energyYear) 
    : [];
  
  const filteredData = validData
    .filter(item => {
      const startYear = parseInt(item.energyYear.split('-')[0], 10);
      return startYear >= 2022;
    })
    .sort((a, b) => {
      const yearA = parseInt(a.energyYear.split('-')[0], 10);
      const yearB = parseInt(b.energyYear.split('-')[0], 10);
      return yearB - yearA; // Newest first
    });

  if (filteredData.length === 0) {
    return (
      <div className="enhanced-grouped-gas-comparison-monthly">
        <h3>Monthly Gas Usage by Energy Year (2022 onwards)</h3>
        <p>No gas comparison data available.</p>
      </div>
    );
  }

  return (
    <div className="enhanced-grouped-gas-comparison-monthly">
      <h3>Monthly Gas Usage by Energy Year (2022 onwards)</h3>
      
      <div className="conversion-info">
        <p><strong>Conversion:</strong> 1 GJ = 32.68 m³ aardgas</p>
      </div>
      
      {filteredData.map(yearData => {
        if (!yearData.monthlyData || yearData.monthlyData.length === 0) {
          return (
            <div key={yearData.energyYear} className="enhanced-year-section">
              <h4>{yearData.energyYear}</h4>
              <p>No monthly data available for this energy year.</p>
            </div>
          );
        }

        // Sort monthly data by date (July to June order)
        const sortedMonthlyData = [...yearData.monthlyData].sort((a, b) => {
          // For energy year sorting: July comes first, June comes last
          const getMonthOrder = (month) => {
            // July = 1, August = 2, ..., June = 12
            return month >= 7 ? month - 6 : month + 6;
          };
          
          if (a.year !== b.year) return a.year - b.year;
          return getMonthOrder(a.month) - getMonthOrder(b.month);
        });

        // Calculate year totals
        const yearTotals = sortedMonthlyData.reduce((totals, month) => ({
          kraagGJ: totals.kraagGJ + (month.kraagUsage || 0),
          torenGJ: totals.torenGJ + (month.torenUsage || 0)
        }), { kraagGJ: 0, torenGJ: 0 });

        // Convert year totals to m³
        const yearTotalsM3 = {
          kraagM3: convertGJtoM3(yearTotals.kraagGJ),
          torenM3: convertGJtoM3(yearTotals.torenGJ)
        };

        const yearKraagPerApt = (yearTotals.kraagGJ / KRAAG_APARTMENTS).toFixed(2);
        const yearTorenPerApt = (yearTotals.torenGJ / TOREN_APARTMENTS).toFixed(2);
        const yearDifference = calculatePercentageDifference(yearKraagPerApt, yearTorenPerApt);
        const yearVerhouding = calculateVerhouding(yearTotals.kraagGJ, yearTotals.torenGJ);

        return (
          <div key={yearData.energyYear} className="enhanced-year-section">
            <h4>
              {yearData.energyYear}
              <span className="enhanced-year-totals">
                GJ: Kraag {formatGJ(yearTotals.kraagGJ)} | Toren {formatGJ(yearTotals.torenGJ)} | 
                m³: Kraag {formatM3(yearTotalsM3.kraagM3)} | Toren {formatM3(yearTotalsM3.torenM3)}
              </span>
            </h4>
            
            <div className="table-responsive">
              <table className="enhanced-year-gas-table">
                <thead>
                  <tr>
                    <th>Month</th>
                    <th>Kraag (GJ)</th>
                    <th>Toren (GJ)</th>
                    <th>Kraag (m³)</th>
                    <th>Toren (m³)</th>
                    <th>Kraag/woning</th>
                    <th>Toren/woning</th>
                    <th>Verschil %</th>
                    <th>Verhouding</th>
                  </tr>
                </thead>
                <tbody>
                  {sortedMonthlyData.map((monthData) => {
                    const kraagGJ = monthData.kraagUsage || 0;
                    const torenGJ = monthData.torenUsage || 0;
                    const kraagM3 = convertGJtoM3(kraagGJ);
                    const torenM3 = convertGJtoM3(torenGJ);
                    
                    const kraagPerApt = (kraagGJ / KRAAG_APARTMENTS).toFixed(2);
                    const torenPerApt = (torenGJ / TOREN_APARTMENTS).toFixed(2);
                    const difference = calculatePercentageDifference(kraagPerApt, torenPerApt);
                    const verhouding = calculateVerhouding(kraagGJ, torenGJ);
                    const diffClass = getDifferenceClass(difference);
                    
                    return (
                      <tr key={`${monthData.year}-${monthData.month}`}>
                        <td>{formatMonthName(monthData.month, monthData.year)}</td>
                        <td>{formatGJ(kraagGJ)}</td>
                        <td>{formatGJ(torenGJ)}</td>
                        <td className="m3-column">{formatM3(kraagM3)}</td>
                        <td className="m3-column">{formatM3(torenM3)}</td>
                        <td>{kraagPerApt}</td>
                        <td>{torenPerApt}</td>
                        <td className={diffClass}>{difference}</td>
                        <td className="verhouding-column">{verhouding}</td>
                      </tr>
                    );
                  })}
                  {/* Year total row */}
                  <tr className="enhanced-year-total-row">
                    <td><strong>Total {yearData.energyYear}</strong></td>
                    <td><strong>{formatGJ(yearTotals.kraagGJ)}</strong></td>
                    <td><strong>{formatGJ(yearTotals.torenGJ)}</strong></td>
                    <td className="m3-column"><strong>{formatM3(yearTotalsM3.kraagM3)}</strong></td>
                    <td className="m3-column"><strong>{formatM3(yearTotalsM3.torenM3)}</strong></td>
                    <td><strong>{yearKraagPerApt}</strong></td>
                    <td><strong>{yearTorenPerApt}</strong></td>
                    <td className={getDifferenceClass(yearDifference)}><strong>{yearDifference}</strong></td>
                    <td className="verhouding-column"><strong>{yearVerhouding}</strong></td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        );
      })}
      
      <div className="enhanced-comparison-legend">
        <p><strong>Legend:</strong></p>
        <ul>
          <li><strong>Kraag:</strong> 14 apartments (201A-G, 203A-G)</li>
          <li><strong>Toren:</strong> 48 apartments (211-305)</li>
          <li><strong>GJ:</strong> Gigajoules - unit of energy measurement</li>
          <li><strong>m³:</strong> Cubic meters of natural gas (1 GJ = 32.68 m³)</li>
          <li><strong>Verschil %:</strong> Percentage difference between Kraag and Toren usage per apartment</li>
          <li><strong>Verhouding:</strong> Usage ratio - Kraag% | Toren% of total monthly consumption</li>
          <li className="positive-change">Positive values (red): Kraag uses more per apartment than Toren</li>
          <li className="negative-change">Negative values (green): Kraag uses less per apartment than Toren</li>
        </ul>
      </div>
      
      <div className="enhanced-data-summary">
        <p><strong>Data Range:</strong> Energy years 2022-2023 onwards (July to June)</p>
        <p><strong>Energy Years Included:</strong> {filteredData.map(d => d.energyYear).join(', ')}</p>
        <p><strong>Conversion Factor:</strong> 1 GJ = 32.68 m³ aardgas (Nederlands standard)</p>
      </div>
    </div>
  );
};
  
  // New component to display Gas Comparison monthly data
  const GasComparisonMonthly = ({ gasComparisonData }) => {
    // Constants for number of apartments
    const KRAAG_APARTMENTS = 14;
    const TOREN_APARTMENTS = 48;
    
    // Make sure gasComparisonData is an array and filter out any items without energyYear
    const validData = Array.isArray(gasComparisonData) 
      ? gasComparisonData.filter(item => item && item.energyYear) 
      : [];
    
    // If no data, don't render anything
    if (validData.length === 0) {
      return null;
    }
    
    // Sort by energy year in ascending order (oldest first)
    const sortedData = [...validData].sort((a, b) => {
      const yearA = parseInt(a.energyYear.split('-')[0], 10);
      const yearB = parseInt(b.energyYear.split('-')[0], 10);
      return yearA - yearB;
    });
    
    // Get the most recent energy year data
    const mostRecentYear = sortedData[sortedData.length - 1];
    
    // If no monthly data, don't render
    if (!mostRecentYear.monthlyData || mostRecentYear.monthlyData.length === 0) {
      return null;
    }
    
    // Format month names for display
    const formatMonthName = (month) => {
      const monthNames = [
        'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 
        'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'
      ];
      return monthNames[month - 1];
    };
    
    // Sort monthly data by date
    const sortedMonthlyData = [...mostRecentYear.monthlyData].sort((a, b) => {
      if (a.year !== b.year) return a.year - b.year;
      return a.month - b.month;
    });
    
    return (
      <div className="gas-comparison-monthly">
        <h3>Monthly Gas Usage for {mostRecentYear.energyYear}</h3>
        
        <div className="table-responsive">
          <table className="month-table">
            <thead>
              <tr>
                <th>Month</th>
                <th>Kraag (GJ)</th>
                <th>Toren (GJ)</th>
                <th>Kraag/woning</th>
                <th>Toren/woning</th>
                <th>Verschil %</th>
              </tr>
            </thead>
            <tbody>
              {sortedMonthlyData.map((month) => {
                const kraagPerApt = (month.kraagUsage / KRAAG_APARTMENTS).toFixed(2);
                const torenPerApt = (month.torenUsage / TOREN_APARTMENTS).toFixed(2);
                
                // Calculate percentage difference
                let difference = 'N/A';
                let diffClass = '';
                
                if (month.kraagUsage !== undefined && month.torenUsage !== undefined && month.torenUsage > 0) {
                  const diff = ((kraagPerApt - torenPerApt) / torenPerApt) * 100;
                  difference = `${diff.toFixed(1)}%`;
                  diffClass = diff < 0 ? 'negative-change' : 'positive-change';
                }
                
                return (
                  <tr key={`${month.year}-${month.month}`}>
                    <td>{formatMonthName(month.month)} {month.year}</td>
                    <td>{month.kraagUsage.toFixed(2)}</td>
                    <td>{month.torenUsage.toFixed(2)}</td>
                    <td>{kraagPerApt}</td>
                    <td>{torenPerApt}</td>
                    <td className={diffClass}>{difference}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    );
  };
  
  return (
    <div className="kenter-admin">
      <div className="admin-header">
        <h1>Energy Admin</h1>
        <Link to="/energie" className="back-link">← Terug naar dashboard</Link>
      </div>
      
      {/* Kenter API Section */}
      <div className="admin-section">
        <h2>Kenter API Sync</h2>
        <p className="section-description">
    Hiermee kan je het totaal verbruik in m³ van het gehele gebouw ophalen per maand (Kraag en Toren samen) bij Kenter. Kenter heeft deze data meestal op de 3e of 4e dag van de maand.
  </p>
        <div className="sync-controls">
          <div className="control-group">
            <label htmlFor="months-input">
              Months to sync:
            </label>
            <input
              id="months-input"
              type="number"
              value={months}
              onChange={(e) => setMonths(parseInt(e.target.value))}
              min="1"
              max="48"
              className="form-input"
            />
          </div>
          
          <button
            onClick={handleSync}
            disabled={isSyncing}
            className="sync-button"
            aria-label="Sync with Kenter API"
          >
            {isSyncing && activeSection === 'kenter' ? 'Syncing...' : 'Sync with Kenter API'}
          </button>
        </div>
        
        {lastUpdate && (
        <div className="last-update-info">
          <p>Last update: <strong>{lastUpdate}</strong></p>
          <p className="update-note">
            Note: This timestamp is only updated when new data is imported.
          </p>
        </div>
        )}
        
        {/* Show logs directly below the kenter section when it's active */}
        {renderLogSection('kenter', syncLog)}
      </div>
      
      {/* ENHANCED Gas Comparison API Section */}
{/* ENHANCED Gas Comparison API Section */}
<div className="admin-section">
  <h2>Gas Comparison Sync</h2>
  <p className="section-description">
    Hiermee haal je maandelijks het verbruik van de Kraag en Toren op in GigaJoule bij Ista. Hiermee kun je het verbruik tussen de Kraag en Toren mee vergelijken.
  </p>
  {/* Success Message */}
  {showSuccessMessage && successStats && (
    <div className="success-message">
      <div className="success-header">
        <p className="success-title">
          ✅ Gas comparison data updated successfully!
        </p>
        <button 
          onClick={() => setShowSuccessMessage(false)}
          className="close-button"
        >
          ×
        </button>
      </div>
      <div className="success-details">
        <p>Updated {successStats.updatedYears?.length} energy years: {successStats.updatedYears?.join(', ')}</p>
        <p>Processed {successStats.processedMonths} months of data in {successStats.processingTime}</p>
        
        {/* Show future month info if relevant */}
        {(successStats.futureMonthsSkipped > 0 || successStats.futureMonthErrors > 0) && (
          <p className="future-month-note">
            Note: {successStats.futureMonthsSkipped + (successStats.futureMonthErrors || 0)} future months were skipped as data isn't available yet.
          </p>
        )}
      </div>
    </div>
  )}
  
  <div className="sync-controls">
    <div className="control-group">
      <label htmlFor="gas-months-input">
        Months to sync:
      </label>
      <select
        id="gas-months-input"
        value={months}
        onChange={(e) => setMonths(parseInt(e.target.value))}
        className="form-input"
      >
        <option value="0">All available data</option>
        <option value="1">Last 1 month</option>
        <option value="2">Last 2 months</option>
        <option value="3">Last 3 months</option>
        <option value="6">Last 6 months</option>
        <option value="12">Last 12 months</option>
        <option value="14">Last 14 months</option>
        <option value="24">Last 24 months</option>
      </select>
      
      {/* Add helpful explanation */}
      <p className="months-info">
        {months === 0 
          ? "This will refresh all historical data (may take several minutes)"
          : `This will refresh data from ${dayjs().subtract(months, 'month').format('MMMM YYYY')} to present`}
      </p>
      
      {/* Add recommendation based on the current data status */}
      {gasComparisonLastMonth && gasComparisonLastMonth.lastMonth && gasComparisonLastMonth.lastYear && (
        <div className="recommendation-box">
          <p>
            <strong>Recommendation:</strong> {(() => {
              const currentDate = dayjs();
              const lastDataDate = dayjs(`${gasComparisonLastMonth.lastYear}-${gasComparisonLastMonth.lastMonth.toString().padStart(2, '0')}-01`);
              const monthsDiff = currentDate.diff(lastDataDate, 'month');
              
              if (monthsDiff <= 0) {
                return "Your data is up to date. No action needed.";
              } else if (monthsDiff === 1) {
                return "Update 1 month to get the latest data.";
              } else if (monthsDiff <= 3) {
                return `Update ${monthsDiff} months to get the latest data.`;
              } else {
                return `Data is ${monthsDiff} months behind. Consider updating at least the most recent 3 months.`;
              }
            })()}
          </p>
        </div>
      )}
    </div>
    
    <button
      onClick={handleRefreshGasComparison}
      disabled={isSyncing}
      className="sync-button"
      aria-label="Refresh Gas Comparison"
    >
      {isSyncing && activeSection === 'gas' ? 'Processing...' : '🔄 Refresh Gas Comparison'}
    </button>
  </div>
  
  {/* Enhanced info display about last available data */}
  {gasComparisonLastMonth && (
    <div className="last-update-info">
      <div className="data-status-box">
        <p className="status-header">
          📊 Data Status:
        </p>
        <ul className="status-details">
          <li>
            <strong>Latest data:</strong> {
              gasComparisonLastMonth.lastMonth && gasComparisonLastMonth.lastYear ? 
                `${getMonthName(gasComparisonLastMonth.lastMonth)} ${gasComparisonLastMonth.lastYear}` : 
                'No data available'
            }
            {(!gasComparisonLastMonth.lastMonth || !gasComparisonLastMonth.lastYear) && 
              gasComparisonLastMonth.lastUpdated && (
                <span className="status-note">
                  {' '}(Last sync attempt: {new Date(gasComparisonLastMonth.lastUpdated).toLocaleDateString('nl-NL')})
                </span>
              )
            }
          </li>
          {gasComparisonLastMonth.lastMonth && gasComparisonLastMonth.lastYear && (
            <li>
              <strong>Status:</strong> <span className={getMonthsNeededText().includes('behind') ? 'status-behind' : 'status-current'}>
                {getMonthsNeededText()}
              </span>
            </li>
          )}
          {gasComparisonLastMonth.lastUpdated && (
            <li>
              <strong>Last processed:</strong> {new Date(gasComparisonLastMonth.lastUpdated).toLocaleDateString('nl-NL')}
            </li>
          )}
        </ul>
      </div>
    </div>
  )}
  
  {/* Future month explanation - show when errors are present in the log */}
  {activeSection === 'gas' && syncLog && (
    syncLog.includes('future') || syncLog.includes('Future') || 
    (successStats && (successStats.futureMonthsSkipped > 0 || successStats.futureMonthErrors > 0))
  ) && (
    <div className="future-month-explanation">
      <div className="status-note-box">
        <p>
          <strong>About errors in future months:</strong> When refreshing gas comparison data, the system tries to get data for all months in the selected range. Errors for future months are normal and expected since that data doesn't exist yet. The system automatically skips these months.
        </p>
      </div>
    </div>
  )}
  
  {/* Show logs directly below the gas section when it's active */}
  {activeSection === 'gas' && syncLog && (
    <div className="sync-log">
      <h3>Gas Comparison Sync Log</h3>
      <div className="log-container">
        <pre>{syncLog}</pre>
      </div>
    </div>
  )}
</div>
      
      {/* Degree Days Section */}
      <div className="admin-section">
        <h2>Degree Days Sync</h2>
         <p className="section-description">
    Hiermee kan je dagelijks de graaddagen en temperatuur ophalen bij OpenMeteo. We gebruiken deze data om het energieverbruik te normaliseren.
  </p>
        <div className="stats-counter">
          <p>
            Current records: <strong>{degreeDaysCount}</strong>
          </p>
          {degreeDaysLastUpdate && (
            <p>
              Data updated through: <strong>{degreeDaysLastUpdate}</strong>
            </p>
          )}
        </div>
        
        <p className="info-text">
          Note: The Archive API only provides data up to the day before yesterday.
        </p>
        
        <div className="sync-controls">
          <div className="date-range-controls">
            <div className="control-group">
              <label htmlFor="start-date">
                Start Date:
              </label>
              <input
                id="start-date"
                type="date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                className="date-input form-input"
              />
            </div>
            
            <div className="control-group">
              <label htmlFor="end-date">
                End Date:
              </label>
              <input
                id="end-date"
                type="date"
                value={endDate}
                onChange={(e) => {
                  const newDate = e.target.value;
                  // If date is too recent, cap at day before yesterday
                  if (isDateTooRecent(newDate)) {
                    const twoDaysAgo = dayjs().subtract(2, 'day').format('YYYY-MM-DD');
                    setEndDate(twoDaysAgo);
                    alert(`End date cannot be more recent than ${twoDaysAgo} (API limitation)`);
                  } else {
                    setEndDate(newDate);
                  }
                }}
                max={dayjs().subtract(2, 'day').format('YYYY-MM-DD')}
                className="date-input form-input"
              />
            </div>
          </div>
          
          <button
            onClick={handleSyncDegreeDays}
            disabled={isDaysSyncing}
            className="sync-button degree-days-button"
            aria-label="Sync Degree Days"
          >
            {isDaysSyncing ? 'Syncing...' : 'Sync Degree Days'}
          </button>
        </div>
        
        {/* Show logs directly below the degree days section when it's active */}
        {renderLogSection('degreedays', daysSyncLog)}
        
        {degreeDaysData && (
          <div className="degree-days-summary">
            <h3>Degree Days Summary</h3>
            <div className="table-responsive">
              <table className="usage-table">
                <thead>
                  <tr>
                    <th>Energy Year</th>
                    <th>Degree Days</th>
                    <th>Avg Temp</th>
                    <th>Usage (m³)</th>
                    <th>Effic.</th>
                    <th>Efficiency per woning</th>
                  </tr>
                </thead>
                <tbody>
                  {degreeDaysData.map((item, index) => {
                    const totalUsage = getTotalUsageForEnergyYear(item._id);
                    const efficiency = calculateEfficiency(totalUsage, item.totalDegreeDays);
                    const efficiencyPerApartment = totalUsage && item.totalDegreeDays ? 
                      ((totalUsage / item.totalDegreeDays) / 62).toFixed(2) : null;
                    
                    // Determine efficiency class based on value
                    let efficiencyClass = '';
                    if (efficiencyPerApartment) {
                      const effValue = parseFloat(efficiencyPerApartment);
                      if (effValue < 0.3) {
                        efficiencyClass = 'negative-change'; // Green for good efficiency
                      } else if (effValue >= 0.3 && effValue <= 0.5) {
                        efficiencyClass = ''; // Default color for average
                      } else {
                        efficiencyClass = 'positive-change'; // Red for poor efficiency
                      }
                    }
                    
                    return (
                      <tr key={index}>
                        <td>{item._id}</td>
                        <td>{formatNumber(item.totalDegreeDays)}</td>
                        <td>{item.avgTemperature.toFixed(1)}°C</td>
                        <td>{totalUsage ? formatNumber(totalUsage) : 'N/A'}</td>
                        <td>{efficiency || 'N/A'}</td>
                        <td className={efficiencyClass}>
                          {efficiencyPerApartment || 'N/A'}
                          {efficiencyPerApartment && 
                            <span className="efficiency-rating">
                              {parseFloat(efficiencyPerApartment) < 0.3 ? ' (Zeer efficiënt)' : 
                               parseFloat(efficiencyPerApartment) <= 0.5 ? ' (Gem.)' : 
                               ' (Inefficiënt)'}
                            </span>
                          }
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            <div className="efficiency-legend">
              <p><strong>Efficiency per woning:</strong></p>
              <ul>
                <li><span className="negative-change">&lt; 0,3 m³/graaddag</span>: Zeer efficiënt (goed geïsoleerd, zuinig gedrag)</li>
                <li><span>0,3 – 0,5 m³/graaddag</span>: Gemiddeld (typisch voor oudere flats of matige isolatie)</li>
                <li><span className="positive-change">&gt; 0,5 m³/graaddag</span>: Inefficiënt (veel verlies of hoge stookbehoefte)</li>
              </ul>
            </div>
          </div>
        )}
      </div>
      
      {/* Energy Usage Data Section */}
      {usageData && (
        <div className="admin-section">
          <div className="section-header-row">
            <h2>Energy Usage Data</h2>
            <div className="toggle-container">
              <button 
                onClick={() => setShowRawData(!showRawData)}
                className="toggle-button"
                aria-label={showRawData ? "Show Summary" : "Show Raw Data"}
              >
                {showRawData ? 'Show Summary' : 'Show Raw Data'}
              </button>
            </div>
          </div>
          
          {showRawData ? (
            <div className="raw-data">
              <pre>{JSON.stringify(usageData, null, 2)}</pre>
            </div>
          ) : (
            <div className="usage-summary">
              <div className="table-responsive">
                <table className="usage-table">
                  <thead>
                    <tr>
                      <th>Energy Year</th>
                      <th>Total Usage</th>
                      <th>Monthly Avg</th>
                      <th>Prediction</th>
                      <th>YoY Change</th>
                    </tr>
                  </thead>
                  <tbody>
                  {Object.keys(usageData).sort().map(year => {
                      const yearData = usageData[year];
                      const monthCount = Object.keys(yearData.monthly || {}).length;
                      const monthlyAvg = monthCount > 0 ? yearData.total / monthCount : 0;
                      
                      // Get previous year data for comparison
                      const [startYear] = year.split('-').map(Number);
                      const prevYear = `${startYear-1}-${startYear}`;
                      const prevYearData = usageData[prevYear];
                      const prevTotal = prevYearData ? prevYearData.total : 0;
                      
                      // Calculate YoY change
                      const prediction = yearData.prediction || yearData.total;
                      const yoyChange = calculatePercentChange(prediction, prevTotal);
                      
                      return (
                        <tr key={year}>
                          <td>{year}</td>
                          <td>{formatNumber(yearData.total)} m³</td>
                          <td>{formatNumber(monthlyAvg)} m³</td>
                          <td>{formatNumber(yearData.prediction || 'N/A')} m³</td>
                          <td className={parseFloat(yoyChange) < 0 ? 'negative-change' : 'positive-change'}>
                            {yoyChange !== '∞' ? `${yoyChange}%` : 'N/A'}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
              
              {/* Use the improved monthly breakdown component */}
              <ImprovedMonthlyBreakdown usageData={usageData} formatNumber={formatNumber} />

              <ImprovedMonthlyBreakdownWithDegreeDays usageData={usageData} formatNumber={formatNumber} />
            </div>
          )}
        </div>
      )}
      
      {/* Gas Comparison Section */}
  <div className="admin-section">
  <div className="section-header-row">
    <h2>Gas Usage Comparison (Kraag vs Toren)</h2>
  </div>
  
  {/* New explanation box */}
  <div className="info-box">
    <p>
      Het gemiddelde warmteverbruik voor huishoudens met stadswarmte (of blokverwarming) is 35 GJ per jaar, plus 6,6 GJ voor warm water.
      Het energieverbruik voor het verwarmen van je huis is afhankelijk van de grootte van de woning, niet van het aantal personen dat er woont. Ook speelt mee hoe goed je huis geïsoleerd is en hoe vaak je de verwarming aanzet. Eén GJ komt overeen met 32,68 m3 aardgas.
    </p>
    <p>
      <strong>Het gemiddelde verbruik in Nederland is 24 GJ voor een appartement.</strong>
    <br/></p>

   
  
  </div>
        
        <div className="gas-comparison-data">
          {gasComparisonData ? (
            Array.isArray(gasComparisonData) && gasComparisonData.length > 0 ? (
              <>
                <GasComparisonTable gasComparisonData={gasComparisonData} />
                <GasComparisonMonthly gasComparisonData={gasComparisonData} />
                <EnhancedGroupedGasComparisonMonthly gasComparisonData={gasComparisonData} />
              </>
            ) : (
              <p>No gas comparison data available. Use the "Refresh Gas Comparison" button to fetch data.</p>
            )
          ) : (
            <p>Loading gas comparison data...</p>
          )}
        </div>
      </div>
      
    </div>
  );
};

export default KenterAdmin;